/**
 * Custom Upload Input Web Component
 * Usage: <custom-upload-input label="Upload Image" name="file_name" accept="image/*" required></custom-upload-input>
 */
// Gemini AI supported MIME types
const GEMINI_SUPPORTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
];

const ERROR_MESSAGES = {
  required: "This field is required",
  invalidType:
    "Invalid file type. Please upload PNG, JPEG, JPG, or PDF only.",
};

class CustomUploadInput extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._elements = {};
    this._isValidType = true;
  }

  connectedCallback() {
    this.render();
    this.cacheElements();
    this.setupEventListeners();
  }

  render() {
    const label = this.getAttribute("label") || "Upload File";
    const name = this.getAttribute("name") || "file";
    const accept = this.getAttribute("accept") || "*";
    const required = this.hasAttribute("required");
    const customIcon = this.getAttribute("icon");
    const defaultIcon = `<svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="100" fill="#EFEFEF"/>
      <path d="M78 71.7778V28.2222C78 24.8 75.2 22 71.7778 22H28.2222C24.8 22 22 24.8 22 28.2222V71.7778C22 75.2 24.8 78 28.2222 78H71.7778C75.2 78 78 75.2 78 71.7778ZM39.1111 54.6667L46.8889 64.0311L57.7778 50L71.7778 68.6667H28.2222L39.1111 54.6667Z" fill="#CCCCCC"/>
    </svg>`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }

        .custom-upload {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }

        .custom-upload__label {
          font-size: 14px;
          font-weight: 500;
          color: #111;
          margin: 0;
        }

        .custom-upload__label.required::after {
          content: '*';
          color: red;
          margin-left: 5px;
        }

        .custom-upload__box {
          display: flex;
          align-items: center;
          gap: 24px;
          min-height: 50px;
          padding: 20px;
          border: 1px solid #979797;
          border-radius: 0px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .custom-upload__box:hover {
          border-color: #111;
          background: #f5f5f5;
        }

        .custom-upload__box.has-file {
          border-color: #111;
        }

        .custom-upload__box.error {
          border-color: #df280a;
        }

        .custom-upload__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .custom-upload__icon svg {
          width: 100px;
          height: 100px;
        }

        .custom-upload__text {
          font-size: 14px;
          font-weight: 500;
          color: #111;
          text-align: center;
          word-break: break-all;
        }

        .custom-upload__input {
          display: none !important;
        }

        .validation-advice {
          color: #df280a;
          font-size: 12px;
          margin-top: 4px;
        }
      </style>

      <div class="custom-upload">
        <label class="custom-upload__label ${required ? "required" : ""}">
          ${label}
        </label>

        <div class="custom-upload__box" data-upload-trigger>
          <div class="custom-upload__icon">${customIcon || defaultIcon}</div>
          <span class="custom-upload__text">CHOOSE FILE</span>
        </div>

        <input
          type="file"
          class="custom-upload__input"
          name="${name}"
          accept="${accept}"
          ${required ? "required" : ""}
        />
      </div>
    `;
  }

  cacheElements() {
    const shadow = this.shadowRoot;
    this._elements = {
      trigger: shadow.querySelector("[data-upload-trigger]"),
      input: shadow.querySelector("input[type='file']"),
      text: shadow.querySelector(".custom-upload__text"),
      box: shadow.querySelector(".custom-upload__box"),
      container: shadow.querySelector(".custom-upload"),
    };
  }

  setupEventListeners() {
    const { trigger, input, text, box } = this._elements;
    if (!trigger || !input || !text || !box) return;

    trigger.addEventListener("click", () => input.click());

    input.addEventListener("change", () => {
      const file = input.files?.[0];

      if (file) {
        // Validate file type for Gemini AI compatibility
        this._isValidType = GEMINI_SUPPORTED_TYPES.includes(file.type);

        if (this._isValidType) {
          text.textContent = file.name;
          box.classList.add("has-file");
          box.classList.remove("error");
          this.removeValidationAdvice();

          this.dispatchEvent(
            new CustomEvent("file-selected", {
              detail: { file },
              bubbles: true,
              composed: true,
            })
          );
        } else {
          // Invalid file type - show error and clear selection
          box.classList.add("error");
          text.textContent = "Invalid file type";

          this.removeValidationAdvice();
          const advice = document.createElement("div");
          advice.className = "validation-advice";
          advice.textContent = ERROR_MESSAGES.invalidType;
          this._elements.container.appendChild(advice);

          // Clear the invalid file
          input.value = "";
          this._isValidType = false;

          this.dispatchEvent(
            new CustomEvent("file-error", {
              detail: {
                error: ERROR_MESSAGES.invalidType,
                fileType: file.type,
              },
              bubbles: true,
              composed: true,
            })
          );
        }
      } else {
        this.clearSelection();
        this._isValidType = true;
      }
    });
  }

  clearSelection() {
    const { text, box } = this._elements;
    if (text) text.textContent = "CHOOSE FILE";
    if (box) box.classList.remove("has-file");

    this.dispatchEvent(
      new CustomEvent("file-removed", {
        bubbles: true,
        composed: true,
      })
    );
  }

  removeValidationAdvice() {
    const oldAdvice = this._elements.container?.querySelector(".validation-advice");
    if (oldAdvice) oldAdvice.remove();
  }

  // Public API
  getFile() {
    return this._elements.input?.files?.[0] || null;
  }

  getInput() {
    return this._elements.input;
  }

  isValidType() {
    return this._isValidType;
  }

  validate() {
    const { input, box, container } = this._elements;
    if (!input || !box || !container) return true;

    this.removeValidationAdvice();

    const isRequired = this.hasAttribute("required");
    const hasFile = input.files?.length > 0;
    const file = input.files?.[0];

    // Check required first
    if (isRequired && !hasFile) {
      box.classList.add("error");

      const advice = document.createElement("div");
      advice.className = "validation-advice";
      advice.textContent = ERROR_MESSAGES.required;
      container.appendChild(advice);

      return false;
    }

    // Check file type if file exists
    if (hasFile && file) {
      const validType = GEMINI_SUPPORTED_TYPES.includes(file.type);
      if (!validType) {
        box.classList.add("error");

        const advice = document.createElement("div");
        advice.className = "validation-advice";
        advice.textContent = ERROR_MESSAGES.invalidType;
        container.appendChild(advice);

        this._isValidType = false;
        return false;
      }
    }

    box.classList.remove("error");
    this._isValidType = true;
    return true;
  }

  reset() {
    const { input, box } = this._elements;

    if (input) input.value = "";
    if (box) box.classList.remove("has-file", "error");

    this.clearSelection();
    this.removeValidationAdvice();
  }
}

// Register the custom element
customElements.define("custom-upload-input", CustomUploadInput);
